/**
 * Token types for formula parsing
 */
enum TokenType {
  NUMBER = "NUMBER",
  STRING = "STRING",
  FUNCTION = "FUNCTION",
  VARIABLE = "VARIABLE",
  OPERATOR = "OPERATOR",
  LPAREN = "LPAREN",
  RPAREN = "RPAREN",
  COMMA = "COMMA",
  EOF = "EOF",
}

interface Token {
  type: TokenType;
  value: string;
  position: number;
}

/**
 * Formula parser error
 */
export class FormulaParseError extends Error {
  constructor(
    message: string,
    public position?: number,
  ) {
    super(message);
    this.name = "FormulaParseError";
  }
}

/**
 * AST Node types
 */
export enum ASTNodeType {
  LITERAL = "LITERAL",
  VARIABLE = "VARIABLE",
  BINARY_OPERATION = "BINARY_OPERATION",
  UNARY_OPERATION = "UNARY_OPERATION",
  FUNCTION_CALL = "FUNCTION_CALL",
}

/**
 * Base AST Node interface
 */
export interface ASTNode {
  type: ASTNodeType;
}

/**
 * Literal value node (numbers, strings)
 */
export interface LiteralNode extends ASTNode {
  type: ASTNodeType.LITERAL;
  value: number | string | boolean;
  dataType: "number" | "string" | "boolean";
}

/**
 * Variable reference node
 */
export interface VariableNode extends ASTNode {
  type: ASTNodeType.VARIABLE;
  name: string;
}

/**
 * Binary operation node (arithmetic, comparison)
 */
export interface BinaryOperationNode extends ASTNode {
  type: ASTNodeType.BINARY_OPERATION;
  operator: string;
  left: ASTNode;
  right: ASTNode;
}

/**
 * Unary operation node (negation, positive)
 */
export interface UnaryOperationNode extends ASTNode {
  type: ASTNodeType.UNARY_OPERATION;
  operator: string;
  operand: ASTNode;
}

/**
 * Function call node
 */
export interface FunctionCallNode extends ASTNode {
  type: ASTNodeType.FUNCTION_CALL;
  functionName: string;
  arguments: ASTNode[];
}

/**
 * Formula parse result with AST
 */
export interface FormulaParseResult {
  ast: ASTNode | null;
  error?: string;
  isValid: boolean;
}

/**
 * Token stream - manages a stream of tokens for parsing
 */
export class TokenStream {
  private tokens: Token[] = [];
  private position = 0;

  constructor(tokens: Token[]) {
    this.tokens = tokens;
    this.position = 0;
  }

  /**
   * Get current token
   */
  currentToken(): Token {
    return this.position < this.tokens.length
      ? this.tokens[this.position]
      : { type: TokenType.EOF, value: "", position: 0 };
  }

  /**
   * Advance to next token
   */
  advance(): void {
    if (this.position < this.tokens.length) {
      this.position++;
    }
  }

  /**
   * Peek at next token without advancing
   */
  peekToken(): Token {
    const nextPos = this.position + 1;
    return nextPos < this.tokens.length
      ? this.tokens[nextPos]
      : { type: TokenType.EOF, value: "", position: 0 };
  }

  /**
   * Check if we're at the end of tokens
   */
  isAtEnd(): boolean {
    return this.currentToken().type === TokenType.EOF;
  }

  /**
   * Get current position for error reporting
   */
  getCurrentPosition(): number {
    return this.currentToken().position;
  }
}

/**
 * AST serialization/deserialization utilities
 */
export class ASTSerializer {
  /**
   * Serialize AST to JSON string
   */
  serialize(ast: ASTNode): string {
    return JSON.stringify(ast, null, 2);
  }

  /**
   * Deserialize JSON string to AST
   */
  deserialize(json: string): ASTNode {
    return JSON.parse(json) as ASTNode;
  }

  /**
   * Convert AST back to formula string for display
   */
  astToFormula(ast: ASTNode | null | undefined): string {
    // Handle null/undefined AST
    if (!ast || !ast.type) {
      return "";
    }

    return `=${this.astToFormulaWithPrecedence(ast, 0)}`;
  }

  /**
   * Convert AST to formula string with operator precedence handling
   */
  private astToFormulaWithPrecedence(
    ast: ASTNode,
    parentPrecedence: number,
  ): string {
    switch (ast.type) {
      case ASTNodeType.LITERAL: {
        const literal = ast as LiteralNode;
        if (literal.dataType === "string") {
          return `"${literal.value}"`;
        } else if (literal.dataType === "boolean") {
          return literal.value ? "TRUE" : "FALSE";
        } else {
          return String(literal.value);
        }
      }

      case ASTNodeType.VARIABLE: {
        const variable = ast as VariableNode;
        return variable.name;
      }

      case ASTNodeType.BINARY_OPERATION: {
        const binary = ast as BinaryOperationNode;
        const precedence = this.getOperatorPrecedence(binary.operator);
        const left = this.astToFormulaWithPrecedence(binary.left, precedence);
        const right = this.astToFormulaWithPrecedence(
          binary.right,
          precedence + 1,
        );

        const result = `${left} ${binary.operator} ${right}`;

        // Only add parentheses if this operation has lower precedence than parent
        return precedence < parentPrecedence ? `(${result})` : result;
      }

      case ASTNodeType.UNARY_OPERATION: {
        const unary = ast as UnaryOperationNode;
        const operand = this.astToFormulaWithPrecedence(unary.operand, 100); // High precedence for unary
        return `${unary.operator}${operand}`;
      }

      case ASTNodeType.FUNCTION_CALL: {
        const func = ast as FunctionCallNode;
        const args = func.arguments
          .map((arg) => this.astToFormulaWithPrecedence(arg, 0))
          .join(", ");
        return `${func.functionName}(${args})`;
      }

      default:
        return "";
    }
  }

  /**
   * Get operator precedence for parentheses handling
   */
  private getOperatorPrecedence(operator: string): number {
    switch (operator) {
      case "=":
      case "==":
      case "!=":
      case "<>":
      case "<":
      case ">":
      case "<=":
      case ">=":
        return 1; // Comparison operators (lowest precedence)
      case "+":
      case "-":
        return 2; // Addition/subtraction
      case "*":
      case "/":
        return 3; // Multiplication/division
      case "^":
        return 4; // Exponentiation (highest precedence)
      default:
        return 0;
    }
  }

  /**
   * Validate AST structure - type guard that narrows unknown to ASTNode
   */
  validate(ast: unknown): ast is ASTNode {
    try {
      // First check if it's an object with a type property
      if (!ast || typeof ast !== "object" || !("type" in ast)) {
        return false;
      }

      const node = ast as { type: unknown; [key: string]: unknown };

      switch (node.type) {
        case ASTNodeType.LITERAL: {
          const literal = node as {
            type: string;
            value: unknown;
            dataType: unknown;
          };
          // Validate that value type matches dataType
          if (literal.dataType === "boolean") {
            return typeof literal.value === "boolean";
          } else if (literal.dataType === "string") {
            return typeof literal.value === "string";
          } else if (literal.dataType === "number") {
            return typeof literal.value === "number";
          }
          return false;
        }

        case ASTNodeType.VARIABLE: {
          const variable = node as { type: string; name: unknown };
          return typeof variable.name === "string" && variable.name.length > 0;
        }

        case ASTNodeType.BINARY_OPERATION: {
          const binary = node as {
            type: string;
            operator: unknown;
            left: unknown;
            right: unknown;
          };
          return (
            typeof binary.operator === "string" &&
            this.validate(binary.left) &&
            this.validate(binary.right)
          );
        }

        case ASTNodeType.UNARY_OPERATION: {
          const unary = node as {
            type: string;
            operator: unknown;
            operand: unknown;
          };
          return (
            typeof unary.operator === "string" && this.validate(unary.operand)
          );
        }

        case ASTNodeType.FUNCTION_CALL: {
          const func = node as {
            type: string;
            functionName: unknown;
            arguments: unknown;
          };
          return (
            typeof func.functionName === "string" &&
            Array.isArray(func.arguments) &&
            func.arguments.every((arg) => this.validate(arg))
          );
        }

        default:
          return false;
      }
    } catch {
      return false;
    }
  }
}

/**
 * Formula parser - stateless parser that can be used as a singleton
 * Uses TokenStream for isolated parsing state
 */
export class FormulaParser {
  /**
   * Parse formula into AST
   */
  parse(formula: string): FormulaParseResult {
    try {
      // Remove leading '=' if present
      const cleanFormula = formula.startsWith("=") ? formula.slice(1) : formula;

      // Tokenize the formula
      const tokens = this.tokenize(cleanFormula);
      const tokenStream = new TokenStream(tokens);

      // Parse to AST
      const ast = this.parseExpression(tokenStream);

      return {
        ast,
        isValid: true,
      };
    } catch (error) {
      return {
        ast: null,
        error: error instanceof Error ? error.message : "Unknown error",
        isValid: false,
      };
    }
  }

  /**
   * Validate formula syntax without generating AST
   */
  validate(formula: string): { isValid: boolean; error?: string } {
    try {
      const cleanFormula = formula.startsWith("=") ? formula.slice(1) : formula;
      const tokens = this.tokenize(cleanFormula);
      const tokenStream = new TokenStream(tokens);

      // Try to parse without storing result
      this.parseExpression(tokenStream);

      return { isValid: true };
    } catch (error) {
      return {
        isValid: false,
        error: error instanceof Error ? error.message : "Invalid syntax",
      };
    }
  }

  /**
   * Parse expression (handles operators with precedence)
   */
  private parseExpression(tokenStream: TokenStream): ASTNode {
    return this.parseComparison(tokenStream);
  }

  /**
   * Parse comparison operators (=, <>, <, >, <=, >=)
   */
  private parseComparison(tokenStream: TokenStream): ASTNode {
    let left = this.parseAddition(tokenStream);

    while (
      tokenStream.currentToken().type === TokenType.OPERATOR &&
      ["=", "==", "!=", "<>", "<", ">", "<=", ">="].includes(
        tokenStream.currentToken().value,
      )
    ) {
      const op = tokenStream.currentToken().value;
      tokenStream.advance();
      const right = this.parseAddition(tokenStream);

      left = {
        type: ASTNodeType.BINARY_OPERATION,
        operator: op,
        left,
        right,
      } as BinaryOperationNode;
    }

    return left;
  }

  /**
   * Parse addition and subtraction
   */
  private parseAddition(tokenStream: TokenStream): ASTNode {
    let left = this.parseMultiplication(tokenStream);

    while (
      tokenStream.currentToken().type === TokenType.OPERATOR &&
      ["+", "-"].includes(tokenStream.currentToken().value)
    ) {
      const op = tokenStream.currentToken().value;
      tokenStream.advance();
      const right = this.parseMultiplication(tokenStream);

      left = {
        type: ASTNodeType.BINARY_OPERATION,
        operator: op,
        left,
        right,
      } as BinaryOperationNode;
    }

    return left;
  }

  /**
   * Parse multiplication, division
   */
  private parseMultiplication(tokenStream: TokenStream): ASTNode {
    let left = this.parseExponentiation(tokenStream);

    while (
      tokenStream.currentToken().type === TokenType.OPERATOR &&
      ["*", "/"].includes(tokenStream.currentToken().value)
    ) {
      const op = tokenStream.currentToken().value;
      tokenStream.advance();
      const right = this.parseExponentiation(tokenStream);

      left = {
        type: ASTNodeType.BINARY_OPERATION,
        operator: op,
        left,
        right,
      } as BinaryOperationNode;
    }

    return left;
  }

  /**
   * Parse exponentiation (^)
   */
  private parseExponentiation(tokenStream: TokenStream): ASTNode {
    let left = this.parseUnary(tokenStream);

    if (
      tokenStream.currentToken().type === TokenType.OPERATOR &&
      tokenStream.currentToken().value === "^"
    ) {
      const op = tokenStream.currentToken().value;
      tokenStream.advance();
      const right = this.parseExponentiation(tokenStream); // Right associative

      left = {
        type: ASTNodeType.BINARY_OPERATION,
        operator: op,
        left,
        right,
      } as BinaryOperationNode;
    }

    return left;
  }

  /**
   * Parse unary operators (+ and -)
   */
  private parseUnary(tokenStream: TokenStream): ASTNode {
    if (
      tokenStream.currentToken().type === TokenType.OPERATOR &&
      ["+", "-"].includes(tokenStream.currentToken().value)
    ) {
      const op = tokenStream.currentToken().value;
      tokenStream.advance();
      const operand = this.parseUnary(tokenStream);

      return {
        type: ASTNodeType.UNARY_OPERATION,
        operator: op,
        operand,
      } as UnaryOperationNode;
    }

    return this.parsePrimary(tokenStream);
  }

  /**
   * Parse primary expressions (numbers, strings, variables, functions, parenthesized expressions)
   */
  private parsePrimary(tokenStream: TokenStream): ASTNode {
    const token = tokenStream.currentToken();

    if (token.type === TokenType.NUMBER) {
      tokenStream.advance();
      return {
        type: ASTNodeType.LITERAL,
        value: parseFloat(token.value),
        dataType: "number",
      } as LiteralNode;
    }

    if (token.type === TokenType.STRING) {
      tokenStream.advance();
      return {
        type: ASTNodeType.LITERAL,
        value: token.value,
        dataType: "string",
      } as LiteralNode;
    }

    if (token.type === TokenType.VARIABLE) {
      const variableName = token.value;

      // Check for boolean literals (case-insensitive)
      const upperVariableName = variableName.toUpperCase();
      if (upperVariableName === "TRUE") {
        tokenStream.advance();
        return {
          type: ASTNodeType.LITERAL,
          value: true,
          dataType: "boolean",
        } as LiteralNode;
      }

      if (upperVariableName === "FALSE") {
        tokenStream.advance();
        return {
          type: ASTNodeType.LITERAL,
          value: false,
          dataType: "boolean",
        } as LiteralNode;
      }

      tokenStream.advance();
      return {
        type: ASTNodeType.VARIABLE,
        name: variableName,
      } as VariableNode;
    }

    if (token.type === TokenType.FUNCTION) {
      return this.parseFunction(tokenStream);
    }

    if (token.type === TokenType.LPAREN) {
      tokenStream.advance(); // consume '('
      const result = this.parseExpression(tokenStream);
      if (tokenStream.currentToken().type !== TokenType.RPAREN) {
        throw new FormulaParseError(
          "Expected closing parenthesis",
          tokenStream.getCurrentPosition(),
        );
      }
      tokenStream.advance(); // consume ')'
      return result;
    }

    throw new FormulaParseError(
      `Unexpected token: ${token.value}`,
      token.position,
    );
  }

  /**
   * Parse function calls
   */
  private parseFunction(tokenStream: TokenStream): FunctionCallNode {
    const funcName = tokenStream.currentToken().value;
    tokenStream.advance(); // consume function name

    if (tokenStream.currentToken().type !== TokenType.LPAREN) {
      throw new FormulaParseError(
        "Expected opening parenthesis after function name",
        tokenStream.getCurrentPosition(),
      );
    }
    tokenStream.advance(); // consume '('

    const args: ASTNode[] = [];

    // Handle empty parameter list
    if (tokenStream.currentToken().type !== TokenType.RPAREN) {
      args.push(this.parseExpression(tokenStream));

      while (tokenStream.currentToken().type === TokenType.COMMA) {
        tokenStream.advance(); // consume ','
        args.push(this.parseExpression(tokenStream));
      }
    }

    if (tokenStream.currentToken().type !== TokenType.RPAREN) {
      throw new FormulaParseError(
        "Expected closing parenthesis",
        tokenStream.getCurrentPosition(),
      );
    }
    tokenStream.advance(); // consume ')'

    return {
      type: ASTNodeType.FUNCTION_CALL,
      functionName: funcName,
      arguments: args,
    } as FunctionCallNode;
  }

  /**
   * Tokenize the formula string
   */
  private tokenize(formula: string): Token[] {
    const tokens: Token[] = [];
    let i = 0;

    while (i < formula.length) {
      const char = formula[i];

      // Skip whitespace
      if (/\s/.test(char)) {
        i++;
        continue;
      }

      // Numbers (including decimals)
      if (/\d/.test(char)) {
        let numStr = "";
        while (i < formula.length && /[\d.]/.test(formula[i])) {
          numStr += formula[i];
          i++;
        }
        tokens.push({
          type: TokenType.NUMBER,
          value: numStr,
          position: i - numStr.length,
        });
        continue;
      }

      // Strings (quoted)
      if (char === '"') {
        let str = "";
        i++; // Skip opening quote
        while (i < formula.length && formula[i] !== '"') {
          str += formula[i];
          i++;
        }
        if (i >= formula.length) {
          throw new FormulaParseError("Unterminated string", i);
        }
        i++; // Skip closing quote
        tokens.push({
          type: TokenType.STRING,
          value: str,
          position: i - str.length - 2,
        });
        continue;
      }

      // Functions and variables (alphanumeric + dots + underscores)
      if (/[a-zA-Z_]/.test(char)) {
        let name = "";
        while (i < formula.length && /[a-zA-Z0-9._]/.test(formula[i])) {
          name += formula[i];
          i++;
        }

        // Check if it's followed by '(' (function)
        const nextNonSpace = this.skipWhitespace(formula, i);
        if (nextNonSpace < formula.length && formula[nextNonSpace] === "(") {
          tokens.push({
            type: TokenType.FUNCTION,
            value: name.toUpperCase(),
            position: i - name.length,
          });
        } else {
          tokens.push({
            type: TokenType.VARIABLE,
            value: name.toUpperCase(),
            position: i - name.length,
          });
        }
        continue;
      }

      // Operators
      if (/[+\-*/^<>=!]/.test(char)) {
        let op = char;
        if (i + 1 < formula.length) {
          const nextChar = formula[i + 1];
          if (
            (char === "<" && nextChar === "=") ||
            (char === ">" && nextChar === "=") ||
            (char === "!" && nextChar === "=") ||
            (char === "=" && nextChar === "=")
          ) {
            op += nextChar;
            i++;
          }
        }
        tokens.push({
          type: TokenType.OPERATOR,
          value: op,
          position: i - op.length + 1,
        });
        i++;
        continue;
      }

      // Parentheses and comma
      if (char === "(") {
        tokens.push({ type: TokenType.LPAREN, value: char, position: i });
      } else if (char === ")") {
        tokens.push({ type: TokenType.RPAREN, value: char, position: i });
      } else if (char === ",") {
        tokens.push({ type: TokenType.COMMA, value: char, position: i });
      } else {
        throw new FormulaParseError(`Unexpected character: ${char}`, i);
      }

      i++;
    }

    tokens.push({ type: TokenType.EOF, value: "", position: formula.length });
    return tokens;
  }

  /**
   * Skip whitespace and return next non-space position
   */
  private skipWhitespace(str: string, start: number): number {
    let i = start;
    while (i < str.length && /\s/.test(str[i])) {
      i++;
    }
    return i;
  }
}
